// Local reimplementation of the SLICE of `@eventbrite/eds-tabs` this app uses
// (`CollectionEvents`' upcoming/past switcher: `items=[{value, displayName,
// content}]` + `onTabChange` + `shouldKeepContentMounted`). Marmalade's `Tabs`
// is a different (radix) API; this is a tiny, faithful state-machine port
// (per the eds-catalog TRAP D — port the STATE/behaviour, not just the nav-bar
// look). Extended from the app-marketplace sibling with the two props the
// collection-events call-site needs:
//   - `onTabChange(value)` — fired on tab switch (the app tracks it).
//   - `shouldKeepContentMounted` — when true ALL panels stay mounted (the
//     inactive one is `display:none`) so per-tab state (the EventList
//     pagination) survives a tab switch. When false only the active panel is
//     mounted (matches eds-tabs' default behaviour).
import React, { useState } from 'react';

import styles from './Tabs.module.scss';

export interface TabItem {
    value: number;
    displayName: string;
    content: React.ReactNode;
}

export interface TabsProps {
    items: TabItem[];
    onTabChange?: (value: number) => void;
    shouldKeepContentMounted?: boolean;
    role?: string;
}

export const Tabs: React.FC<TabsProps> = ({
    items,
    onTabChange,
    shouldKeepContentMounted = false,
    role = 'tablist',
}) => {
    const [activeValue, setActiveValue] = useState(items[0]?.value);
    const activeItem =
        items.find((item) => item.value === activeValue) ?? items[0];

    const select = (value: number) => {
        if (value === activeItem?.value) {
            return;
        }
        setActiveValue(value);
        onTabChange?.(value);
    };

    return (
        <div className={styles.tabs} data-spec="app-tabs">
            <div className={styles.navigation} role={role}>
                {items.map((item) => (
                    <button
                        key={item.value}
                        type="button"
                        role="tab"
                        aria-selected={item.value === activeItem?.value}
                        className={
                            item.value === activeItem?.value
                                ? `${styles.tabLink} ${styles.tabLinkActive}`
                                : styles.tabLink
                        }
                        onClick={() => select(item.value)}
                    >
                        <span className={styles.tabLinkText}>
                            {item.displayName}
                        </span>
                    </button>
                ))}
            </div>
            {shouldKeepContentMounted
                ? items.map((item) => (
                      <div
                          key={item.value}
                          className={styles.panel}
                          role="tabpanel"
                          hidden={item.value !== activeItem?.value}
                          style={
                              item.value === activeItem?.value
                                  ? undefined
                                  : { display: 'none' }
                          }
                      >
                          {item.content}
                      </div>
                  ))
                : (
                      <div className={styles.panel} role="tabpanel">
                          {activeItem?.content}
                      </div>
                  )}
        </div>
    );
};

export default Tabs;
